import React from 'react';
import type { MatchEvent } from '@/components/wyscout/MatchEvents/types';
import { eventAccessibleLabel, eventActionLabel, isCardEvent, isChanceEvent, isGoalEvent, isShotEvent, orientEventLocation } from './helpers';
import type { EventPitchProps } from './types';

const markerType = (event: MatchEvent): 'goal' | 'card' | 'shot' | 'chance' | 'generic' => {
  if (isGoalEvent(event)) return 'goal';
  if (isCardEvent(event)) return 'card';
  if (isShotEvent(event)) return 'shot';
  if (isChanceEvent(event)) return 'chance';
  return 'generic';
};

const EventPitch: React.FC<EventPitchProps> = ({
  events,
  homeTeamId,
  awayTeamId,
  homeTeamName,
  awayTeamName,
  selectedEventId,
  onSelectEvent,
}) => {
  const locatedEvents = events.flatMap(event => {
    const location = orientEventLocation(event, awayTeamId);
    return location ? [{ event, location }] : [];
  });

  const selectWithKeyboard = (keyboardEvent: React.KeyboardEvent<SVGGElement>, eventId: number): void => {
    if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') return;
    keyboardEvent.preventDefault();
    onSelectEvent(eventId);
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900" aria-labelledby="event-pitch-title">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 id="event-pitch-title" className="font-semibold text-gray-900 dark:text-gray-100">Event pitch</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">Select a marker to link it to the timeline.</p>
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-300" aria-label="Team marker colours">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" />{homeTeamName}</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-rose-500" />{awayTeamName}</span>
        </div>
      </div>

      <div className="rounded-lg bg-emerald-800 p-2">
        <svg viewBox="0 0 100 68" className="block h-auto w-full" role="group" aria-labelledby="event-pitch-svg-title event-pitch-svg-description">
          <title id="event-pitch-svg-title">Match event locations</title>
          <desc id="event-pitch-svg-description">Home attacks left to right. Away locations are rotated and attack right to left.</desc>
          <rect x="1" y="1" width="98" height="66" rx="1" fill="#167c4c" stroke="white" strokeWidth="0.45" />
          <line x1="50" y1="1" x2="50" y2="67" stroke="white" strokeWidth="0.35" />
          <circle cx="50" cy="34" r="9.15" fill="none" stroke="white" strokeWidth="0.35" />
          <circle cx="50" cy="34" r="0.65" fill="white" />
          <rect x="1" y="13.85" width="16" height="40.3" fill="none" stroke="white" strokeWidth="0.35" />
          <rect x="83" y="13.85" width="16" height="40.3" fill="none" stroke="white" strokeWidth="0.35" />
          <rect x="1" y="23.5" width="5.5" height="21" fill="none" stroke="white" strokeWidth="0.35" />
          <rect x="93.5" y="23.5" width="5.5" height="21" fill="none" stroke="white" strokeWidth="0.35" />
          <circle cx="11" cy="34" r="0.6" fill="white" />
          <circle cx="89" cy="34" r="0.6" fill="white" />

          {locatedEvents.map(({ event, location }) => {
            const kind = markerType(event);
            const isHome = event.team?.id === homeTeamId;
            const isAway = event.team?.id === awayTeamId;
            const teamColor = isHome ? '#3b82f6' : isAway ? '#f43f5e' : '#6b7280';
            const y = location.y * 0.68;
            const selected = event.id === selectedEventId;
            const teamName = isHome ? homeTeamName : isAway ? awayTeamName : event.team?.name || 'Unknown team';
            const label = eventAccessibleLabel(event, teamName);
            const cardIsRed = event.infraction?.redCard || (event.type.secondary || []).includes('red_card');

            return (
              <g
                key={event.id}
                role="button"
                tabIndex={0}
                aria-label={label}
                aria-pressed={selected}
                className="group cursor-pointer focus:outline-none"
                transform={`translate(${location.x} ${y})`}
                onClick={() => onSelectEvent(event.id)}
                onKeyDown={keyboardEvent => selectWithKeyboard(keyboardEvent, event.id)}
              >
                <circle r="5" fill="transparent" />
                <circle className="opacity-0 group-focus:opacity-100" r="4.4" fill="none" stroke="white" strokeWidth="0.8" />
                {selected && <circle r="3.8" fill="none" stroke="#facc15" strokeWidth="0.8" />}
                {kind === 'goal' && <path d="M 0 -3 L 3 0 L 0 3 L -3 0 Z" fill="#16a34a" stroke={teamColor} strokeWidth="0.9" />}
                {kind === 'card' && <rect x="-2.1" y="-3" width="4.2" height="6" rx="0.5" fill={cardIsRed ? '#dc2626' : '#facc15'} stroke={teamColor} strokeWidth="0.7" />}
                {kind === 'shot' && <circle r="2.7" fill={event.shot?.onTarget ? '#ffffff' : teamColor} stroke={teamColor} strokeWidth="0.8" />}
                {kind === 'chance' && <path d="M 0 -3 L 3 2.5 L -3 2.5 Z" fill={teamColor} stroke="white" strokeWidth="0.45" />}
                {kind === 'generic' && <path d="M -2.7 -2 L 0 -3.2 L 2.7 -2 L 2.7 2 L 0 3.2 L -2.7 2 Z" fill="#6b7280" stroke="white" strokeWidth="0.45" />}
                <text y="1.05" textAnchor="middle" fontSize="2.7" fontWeight="700" fill={kind === 'card' && !cardIsRed ? '#713f12' : kind === 'shot' && event.shot?.onTarget ? teamColor : 'white'} aria-hidden="true">
                  {kind === 'goal' ? 'G' : kind === 'card' ? (cardIsRed ? 'R' : 'Y') : kind === 'shot' ? (event.shot?.onTarget ? 'T' : 'S') : kind === 'chance' ? '!' : 'E'}
                </text>
                <title>{eventActionLabel(event)}</title>
              </g>
            );
          })}
        </svg>
      </div>

      <div className="mt-3 flex justify-between text-xs font-medium text-gray-500 dark:text-gray-400">
        <span>{awayTeamName} attacks left</span>
        <span>{homeTeamName} attacks right</span>
      </div>
      {locatedEvents.length === 0 && <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No locations are available for these events.</p>}
    </section>
  );
};

export default EventPitch;
